import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box } from "@mui/material";

import { ActionButton, FormDialog, InputField } from "../v3";
import { toolValidationRules } from "../../pages/tools/validations";
import { ToolService } from "../../services";

export const AddEditTool = ({ open, toolId, storeId, onClose = () => {}, onSuccess = () => {} }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [toolDetails, setToolDetails] = useState({
    name: undefined,
    code: undefined,
    description: undefined,
    totalQuantity: undefined,
    storeId: undefined,
  });

  useEffect(() => {
    if (open && toolId) fetchData(toolId);
    else if (open && !toolId) handleClearForm();
  }, [open, toolId]);

  const fetchData = async () => {
    try {
      const { data: tool } = await ToolService.getToolById(toolId);
      setToolDetails({
        name: tool?.name,
        code: tool?.code,
        description: tool?.description,
        totalQuantity: tool?.totalQuantity,
        storeId: tool?.storeId,
      });
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
      toast.error("Failed to fetch tool details.");
    }
  };

  const updateDetailsState = (event) => {
    const { name, value } = event.target;
    setToolDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === "totalQuantity") {
      const num = parseFloat(value);
      const integerRegex = /^[1-9]\d*$/;

      if (value !== "" && (isNaN(num) || num <= 0)) {
        setErrors((prev) => ({ ...prev, totalQuantity: "Quantity must be greater than 0" }));
      } else if (value !== "" && !integerRegex.test(value)) {
        setErrors((prev) => ({ ...prev, totalQuantity: "Decimal values are not allowed" }));
      } else {
        setErrors((prev) => {
          const { totalQuantity, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleClearForm = () => {
    setToolDetails({
      name: undefined,
      code: undefined,
      description: undefined,
      totalQuantity: undefined,
      storeId: undefined,
    });
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const formatToolPayload = (details, isEdit) => {
    const toolData = { ...details };

    // trim and remove empty strings
    Object.keys(toolData).forEach((key) => {
      if (typeof toolData[key] === "string") {
        toolData[key] = toolData[key].trim();
        if (toolData[key] === "") {
          if (isEdit) toolData[key] = null;
          else delete toolData[key];
        }
      }
    });
    return toolData;
  };

  const handleSaveTool = async () => {
    setErrors({});

    const formattedToolDetails = formatToolPayload(toolDetails, !!toolId);
    const { totalQuantity, ...rest } = formattedToolDetails;
    const payload = {
      ...rest,
      ...(toolId ? {} : { totalQuantity: Number(toolDetails.totalQuantity) }),
      storeId: storeId,
    };

    const errors = toolValidationRules(payload, toolId);
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    try {
      setLoading(true);
      if (toolId) await ToolService.updateTool(toolId, payload);
      else await ToolService.createTool(payload);

      toast.success(`Tool ${toolId ? "updated" : "added"} successfully`);
      handleCloseModal();
      onSuccess?.();
    } catch (error) {
      console.error("🚀 ~ handleAddTool ~ error:", error);
      toast.error(`Failed to ${toolId ? "update" : "add"} tool`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title={`${toolId ? "Edit" : "Add"} Tool`}
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveTool} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Name"
            name="name"
            value={toolDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
          <InputField
            required
            label="Total Quantity"
            name="totalQuantity"
            value={toolDetails.totalQuantity}
            disabled={!!toolId}
            onChange={updateDetailsState}
            error={!!errors.totalQuantity}
            helperText={errors.totalQuantity}
            fullWidth
          />
        </Box>

        <Box display="flex" gap={2}>
          <InputField
            label="Description"
            name="description"
            value={toolDetails.description}
            onChange={updateDetailsState}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
          />
          <InputField
            label="Code"
            name="code"
            value={toolDetails.code}
            onChange={updateDetailsState}
            error={!!errors.code}
            helperText={errors.code}
            fullWidth
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
